// Utilitários de UI do Report de Momento X (fluxo CX) — datas, contagem regressiva, iniciais, foco e ids.
//
// Datas: a API do fluxo devolve ISO 8601 com o offset do servidor (America/Sao_Paulo), ex.
// "2026-09-29T13:00:00-03:00". O que se EXIBE é o horário "de parede" da própria string — o mesmo que as
// listas do Oráculo mostram (texto cru do banco) —, nunca reconvertido pelo fuso do navegador e nunca via
// `toISOString()` (que passa para UTC e erra o dia/horário). Instantes (contagem regressiva, diferença de
// relógio) usam `toTimestamp`, que respeita o offset quando ele vem na string.
import {
  CLOSED_DEAL,
  HAD_MEETING,
  HAD_SPECIALIST_SUPPORT,
  NOT_HELD_REASON,
  NOT_HELD_REASONS,
  OFFER_MADE,
  OFFER_NOT_MADE_REASON,
  OFFER_NOT_MADE_REASONS,
} from '@/constants/appointment_report_v2'
import {
  INTERVIEW,
  NEGOTIATION,
  PARTNERSHIP,
  X_MOMENT,
} from '@/constants/appointment_types'
import { newIdempotencyKey } from '@/helpers/idempotency'

// ---------------------------------------------------------------------------------------------------------------
// Datas
// ---------------------------------------------------------------------------------------------------------------
const WALL_CLOCK = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/
const HAS_OFFSET = /(?:Z|[+-]\d{2}:?\d{2})$/i
const OFFSET_PARTS = /(?:Z|([+-])(\d{2}):?(\d{2}))$/i

const pad2 = value => String(value).padStart(2, '0')

/** Partes do horário "de parede" de uma string ISO/MySQL (`{ year, month, day, hour, minute, second }`) ou null. */
export const parseWallClock = value => {
  if (typeof value !== 'string') return null
  const match = WALL_CLOCK.exec(value.trim())
  if (!match) return null

  return {
    year: match[1],
    month: match[2],
    day: match[3],
    hour: match[4] || '00',
    minute: match[5] || '00',
    second: match[6] || '00',
  }
}

/** "23/02/26" (formato do Figma). */
export const formatShortDate = value => {
  const parts = parseWallClock(value)
  return parts ? `${parts.day}/${parts.month}/${parts.year.slice(2)}` : ''
}

/**
 * A data "de parede" da string é hoje? Com offset na string (ISO da API), "hoje" é contado no mesmo fuso dela (o do
 * servidor), não no do navegador; sem offset, no fuso local.
 */
export const isWallClockToday = (value, now = Date.now()) => {
  const parts = parseWallClock(value)
  if (!parts) return false
  const offset = OFFSET_PARTS.exec(value.trim())
  let today
  if (offset) {
    const sign = offset[1] === '-' ? -1 : 1
    const offsetMinutes = offset[1] ? sign * (Number(offset[2]) * 60 + Number(offset[3])) : 0
    const shifted = new Date(now + offsetMinutes * 60000)
    today = [shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, shifted.getUTCDate()]
  } else {
    const local = new Date(now)
    today = [local.getFullYear(), local.getMonth() + 1, local.getDate()]
  }

  return parts.year === String(today[0]) && parts.month === pad2(today[1]) && parts.day === pad2(today[2])
}

/** "13:00". */
export const formatTime = value => {
  const parts = parseWallClock(value)
  return parts ? `${parts.hour}:${parts.minute}` : ''
}

/** "23/02/26  |  13:00" (cabeçalho do Figma; a central usa `' | '`). */
export const formatDateTime = (value, separator = '  |  ') => {
  const date = formatShortDate(value)
  if (!date) return ''
  return `${date}${separator}${formatTime(value)}`
}

/**
 * O backend grava `appointment_end` com 1 minuto a menos que o escolhido (evita conflito de horário entre
 * compromissos encostados — `AppointmentController::create/update`). Para exibir o horário que o PN marcou
 * (13:00–14:00, e não 13:59), devolve a string com 1 minuto a mais quando o minuto é 59. Só para exibição.
 */
export const appointmentEndForDisplay = value => {
  const parts = parseWallClock(value)
  if (!parts || parts.minute !== '59') return value
  const date = new Date(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute) + 1,
  )
  if (Number.isNaN(date.getTime())) return value

  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`
    + `T${pad2(date.getHours())}:${pad2(date.getMinutes())}:00`
}

/**
 * Instante (ms) de uma data da API. Com offset/Z usa o offset da string; sem offset, interpreta o horário de
 * parede no fuso local (evita o `Date.parse` de "YYYY-MM-DD HH:MM:SS", que é NaN no Safari).
 */
export const toTimestamp = value => {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.getTime()
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value !== 'string' || !value.trim()) return null
  const text = value.trim()
  if (HAS_OFFSET.test(text)) {
    const parsed = Date.parse(text)
    return Number.isFinite(parsed) ? parsed : null
  }
  const parts = parseWallClock(text)
  if (!parts) return null
  const local = new Date(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second),
  )

  return Number.isNaN(local.getTime()) ? null : local.getTime()
}

/**
 * Tempo restante da trava por horário: "2 h 14 min", "45 min", e nos últimos 10 minutos com segundos
 * ("9 min 05 s", "12 s"). Unidades abreviadas do pt-BR (board v4-report). Com mais de 24 h (Momento X agendado para
 * outro dia — o Novo compromisso leva direto para cá), em dias e horas: "6 d 23 h", "2 d" — nunca "167 h 59 min".
 */
export const formatRemaining = totalSeconds => {
  const seconds = Math.max(0, Math.floor(Number(totalSeconds) || 0))
  if (seconds <= 600) {
    const minutes = Math.floor(seconds / 60)
    const rest = seconds % 60
    return minutes ? `${minutes} min ${pad2(rest)} s` : `${rest} s`
  }
  const totalMinutes = Math.ceil(seconds / 60)
  if (totalMinutes > 24 * 60) {
    // Arredonda a hora para cima (como os minutos acima): a contagem nunca promete liberar antes do horário real.
    const totalHours = Math.ceil(totalMinutes / 60)
    const days = Math.floor(totalHours / 24)
    const restHours = totalHours % 24
    return restHours ? `${days} d ${restHours} h` : `${days} d`
  }
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  const parts = []
  if (hours) parts.push(`${hours} h`)
  if (minutes || !hours) parts.push(`${minutes} min`)

  return parts.join(' ')
}

// ---------------------------------------------------------------------------------------------------------------
// Pessoas, tipos e ids
// ---------------------------------------------------------------------------------------------------------------

/** "Emir Salin" → "ES"; um nome só → as duas primeiras letras. */
export const initialsOf = name => {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean)
  if (!words.length) return ''
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()

  return `${words[0].charAt(0)}${words[words.length - 1].charAt(0)}`.toUpperCase()
}

/** Tom do chip de tipo de compromisso (classes `.ar-chip--<tom>` de `_appointment-report.scss`). */
export const eventTypeTone = eventType => {
  switch (Number(eventType)) {
    case X_MOMENT: return 'mx'
    case INTERVIEW: return 'interview'
    case NEGOTIATION: return 'negotiation'
    case PARTNERSHIP: return 'partnership'
    default: return 'neutral'
  }
}

let uidSequence = 0

/** Id único por instância (os ids de label/erro/listbox não podem colidir quando há dois formulários na tela). */
export const nextReportUid = prefix => {
  uidSequence += 1
  return `${prefix || 'ar'}-${uidSequence}`
}

/** UUID v4 (`crypto.getRandomValues`) para o header `Idempotency-Key` — uma por rascunho do report. */
export const newReportIdempotencyKey = () => newIdempotencyKey()

// ---------------------------------------------------------------------------------------------------------------
// Movimento e foco
// ---------------------------------------------------------------------------------------------------------------
export const prefersReducedMotion = () => {
  try {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)
  } catch (error) {
    return false
  }
}

/**
 * Rola até o alvo (centro da tela; suave, exceto com movimento reduzido) e dá foco sem rolar de novo.
 * `target` pode ser um elemento ou um componente com `focus()`.
 */
export const scrollIntoViewAndFocus = target => {
  if (!target) return
  const element = target.$el || target
  if (element && typeof element.scrollIntoView === 'function') {
    element.scrollIntoView({ block: 'center', behavior: prefersReducedMotion() ? 'auto' : 'smooth' })
  }
  if (typeof target.focus === 'function') target.focus({ preventScroll: true })
}

// ---------------------------------------------------------------------------------------------------------------
// Respostas
// ---------------------------------------------------------------------------------------------------------------
const YES_NO_KEYS = [HAD_MEETING, CLOSED_DEAL, OFFER_MADE, HAD_SPECIALIST_SUPPORT]
const REASON_OPTIONS = {
  [NOT_HELD_REASON]: NOT_HELD_REASONS,
  [OFFER_NOT_MADE_REASON]: OFFER_NOT_MADE_REASONS,
}

/** As 6 respostas do formulário, todas vazias (nenhum valor padrão — Figma). */
export const emptyReportAnswers = () => ({
  [HAD_MEETING]: null,
  [CLOSED_DEAL]: null,
  [OFFER_MADE]: null,
  [HAD_SPECIALIST_SUPPORT]: null,
  [NOT_HELD_REASON]: null,
  [OFFER_NOT_MADE_REASON]: null,
})

const toYesNo = value => {
  if (value === true || value === 1 || value === '1' || value === 'true') return true
  if (value === false || value === 0 || value === '0' || value === 'false') return false
  return null
}

const toReason = (key, value) => (REASON_OPTIONS[key].some(option => option.value === value) ? value : null)

/**
 * Respostas vindas do servidor (rascunho) → as 6 chaves conhecidas, com tipos garantidos. Chaves estranhas
 * (outra versão do formulário) e motivos fora da lista são descartados.
 */
export const sanitizeReportAnswers = raw => {
  const source = raw && typeof raw === 'object' ? raw : {}
  const answers = emptyReportAnswers()
  YES_NO_KEYS.forEach(key => {
    answers[key] = toYesNo(source[key])
  })
  Object.keys(REASON_OPTIONS).forEach(key => {
    answers[key] = toReason(key, source[key])
  })

  return answers
}

/** Report gravado (resumo de `presentReport` no backend) → respostas, para o "Resumo do report". */
export const answersFromReport = report => sanitizeReportAnswers(report || {})
